// store/slices/uiSlice.ts
import { createSlice, type PayloadAction } from '@reduxjs/toolkit';
import Cookies from 'js-cookie';
import type { UIState } from '@/types';
import { LANG_KEY, THEME_KEY } from '@/lib/utils/cookies';

const LS_SIDEBAR = 'ui:sidebarOpen';
const LS_LANG    = 'ui:language';
const LS_THEME   = 'ui:theme';

const readBool = (key: string, fallback: boolean): boolean => {
    try {
        const v = localStorage.getItem(key);
        return v === null ? fallback : v === 'true';
    } catch { return fallback; }
};

const readLang = (): 'en' | 'ar' => {
    try {
        const cookie = Cookies.get(LANG_KEY);
        if (cookie === 'ar' || cookie === 'en') return cookie;
        const v = localStorage.getItem(LS_LANG);
        return v === 'ar' ? 'ar' : 'en';
    } catch { return 'en'; }
};

const readTheme = (): 'light' | 'dark' | 'system' => {
    try {
        const cookie = Cookies.get(THEME_KEY);
        if (cookie === 'dark' || cookie === 'system' || cookie === 'light') return cookie;
        const v = localStorage.getItem(LS_THEME);
        if (v === 'dark' || v === 'system' || v === 'light') return v;
        return 'dark';
    } catch { return 'dark'; }
};

const _lang = readLang();
const _theme = readTheme();

// Apply persisted language and theme to the document immediately on module load
if (typeof document !== 'undefined') {
    document.documentElement.dir  = _lang === 'ar' ? 'rtl' : 'ltr';
    document.documentElement.lang = _lang;
    applyTheme(_theme);
}

function applyTheme(theme: 'light' | 'dark' | 'system') {
    if (typeof document === 'undefined') return;
    const root = document.documentElement;
    if (theme === 'system') {
        const prefersDark = window.matchMedia('(prefers-color-scheme: dark)').matches;
        root.classList.toggle('dark', prefersDark);
    } else {
        root.classList.toggle('dark', theme === 'dark');
    }
}

const initialState: UIState = {
    sidebarOpen: readBool(LS_SIDEBAR, true),
    mobileSidebarOpen: false,
    language: _lang,
    theme: _theme,
    loadingStates: {},
};

const uiSlice = createSlice({
    name: 'ui',
    initialState,
    reducers: {
        toggleSidebar: (state) => {
            state.sidebarOpen = !state.sidebarOpen;
            try { localStorage.setItem(LS_SIDEBAR, String(state.sidebarOpen)); } catch { /* noop */ }
        },
        setSidebarOpen: (state, action: PayloadAction<boolean>) => {
            state.sidebarOpen = action.payload;
            try { localStorage.setItem(LS_SIDEBAR, String(action.payload)); } catch { /* noop */ }
        },
        toggleMobileSidebar: (state) => {
            state.mobileSidebarOpen = !state.mobileSidebarOpen;
        },
        setMobileSidebarOpen: (state, action: PayloadAction<boolean>) => {
            state.mobileSidebarOpen = action.payload;
        },
        closeAllSidebars: (state) => {
            state.mobileSidebarOpen = false;
            state.sidebarOpen = false;
        },
        setLanguage: (state, action: PayloadAction<'en' | 'ar'>) => {
            state.language = action.payload;
            try {
                Cookies.set(LANG_KEY, action.payload, { expires: 365, sameSite: 'lax', path: '/' });
                localStorage.setItem(LS_LANG, action.payload);
            } catch { /* noop */ }
            if (typeof document !== 'undefined') {
                document.documentElement.dir = action.payload === 'ar' ? 'rtl' : 'ltr';
                document.documentElement.lang = action.payload;
            }
        },
        setTheme: (state, action: PayloadAction<'light' | 'dark' | 'system'>) => {
            state.theme = action.payload;
            try {
                Cookies.set(THEME_KEY, action.payload, { expires: 365, sameSite: 'lax', path: '/' });
                localStorage.setItem(LS_THEME, action.payload);
            } catch { /* noop */ }
            applyTheme(action.payload);
        },
        setLoadingState: (state, action: PayloadAction<{ key: string; loading: boolean }>) => {
            state.loadingStates[action.payload.key] = action.payload.loading;
        },
        clearLoadingState: (state, action: PayloadAction<string>) => {
            delete state.loadingStates[action.payload];
        },
        clearAllLoadingStates: (state) => {
            state.loadingStates = {};
        },
    },
});

export const {
    toggleSidebar,
    setSidebarOpen,
    toggleMobileSidebar,
    setMobileSidebarOpen,
    closeAllSidebars,
    setLanguage,
    setTheme,
    setLoadingState,
    clearLoadingState,
    clearAllLoadingStates,
} = uiSlice.actions;

export default uiSlice.reducer;